/**
 * Карточка названия тех.операции — эталон карточки культуры (AK-564).
 *
 * Founder 08.08: «по справочнику культур и тех.операций — истории там одни и
 * те же, а UX какой-то не одинаковый». Карточка культуры (`CatalogRecordSheet`)
 * утверждена 08.08 и объявлена эталоном; здесь — те же блоки, тот же порядок,
 * те же формулировки гардов. Отличается содержимым, не устройством:
 *
 *  · как называется у нас (своё название, на эталон не влияет);
 *  · в системе это + класс работы; менять привязку можно, только пока по
 *    названию нет операций/норм/строк стоимости — иначе у них зафиксирован
 *    состав полей, и смена эталона задним числом поменяла бы смысл истории;
 *  · где видно (факт: смена уровня — отдельная операция raise_scope, ADR §O8);
 *  · свои поля — чипы + «+ добавить поле» (решение founder 3 от 10.08:
 *    конструктор переезжает в карточку);
 *  · как используется — числа без ссылок (сезон и хозяйство не заданы);
 *  · в архив / вернуть из архива · заявка в системный каталог.
 *
 * Блока «Коротко о названии» больше нет: он был компенсацией того, что классу
 * работы, видимости и числу полей негде было стоять. Теперь они стоят на своих
 * местах, и сводка стала дублем.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Search, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { formatRuDate } from '@/lib/dateFmt';
import { matchesSearch } from '@/lib/reference/normalizeSearch';
import {
  cotVisibilityLabel,
  operationsCountLabel,
  referenceOpClassLabel,
  referenceTypeLabel,
  useArchiveCompanyOpType,
  useImportQueue,
  useOpTypeReferences,
  useReferenceOpTypes,
  useRemoveExtraField,
  useRequestOpTypeImport,
  useUnarchiveCompanyOpType,
  useUpdateCompanyOpType,
  type CompanyOpType,
  type ExtraFieldScope,
} from '@/lib/reference/companyOperationTypes';
import { fieldSummary } from './opTypeFieldForm';
import { OpTypeFieldDialog } from './OpTypeFieldDialog';

export function CompanyOpTypeSheet({
  item,
  open,
  canWrite,
  canArchive,
  onOpenChange,
  onArchiveAndCreate,
}: {
  item: CompanyOpType | null;
  open: boolean;
  canWrite: boolean;
  canArchive: boolean;
  onOpenChange: (open: boolean) => void;
  /** Рабочий выход из гарда перепривязки: старое в архив, форма на создание. */
  onArchiveAndCreate?: (item: CompanyOpType) => void;
}) {
  const [name, setName] = useState('');
  const [relinkQuery, setRelinkQuery] = useState('');
  const [fieldDialog, setFieldDialog] = useState(false);

  const update = useUpdateCompanyOpType();
  const archive = useArchiveCompanyOpType();
  const unarchive = useUnarchiveCompanyOpType();
  const removeField = useRemoveExtraField();
  const requestImport = useRequestOpTypeImport();
  const { data: refTypes } = useReferenceOpTypes();
  const refs = useOpTypeReferences(open && item ? item.id : null);
  const queue = useImportQueue('all');

  useEffect(() => {
    if (!open || !item) return;
    setName(item.local_name);
    setRelinkQuery('');
  }, [open, item]);

  const relinkOptions = useMemo(() => {
    if (!relinkQuery.trim()) return [];
    return (refTypes ?? [])
      .map((t) => ({ id: t.id, label: referenceTypeLabel(t) }))
      .filter((o) => matchesSearch(o.label, relinkQuery))
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
      .slice(0, 20);
  }, [refTypes, relinkQuery]);

  if (!item) return null;

  const archived = Boolean(item.archived_at);
  const operations = refs.data?.operations ?? item.usage_count ?? 0;
  const norms = refs.data?.company_work_norms ?? 0;
  const costLines = refs.data?.season_op_cost_lines ?? 0;
  const usedCount = operations + norms + costLines;
  /**
   * Пока счётчик не приехал — перепривязку держим закрытой: иначе человек
   * успевает выбрать эталон и ловит 409 на сохранении вместо объяснения
   * заранее. Это поведение карточки #714, оно правильное — сохраняем.
   */
  const relinkLocked = !refs.isSuccess || usedCount > 0;
  const opClass = referenceOpClassLabel(item.op_class);
  const busy =
    update.isPending || archive.isPending || unarchive.isPending || removeField.isPending;
  const dirty = name.trim() !== item.local_name;

  const request = (queue.data ?? []).find((q) => q.company_op_type_id === item.id);

  const ownFields: Array<{ scope: ExtraFieldScope; def: (typeof item.extra_fields)[number] }> = [
    ...(item.extra_fields ?? []).map((def) => ({ scope: 'operation' as const, def })),
    ...(item.extra_work_fields ?? []).map((def) => ({ scope: 'work' as const, def })),
  ];

  function save(extra?: { ref_id?: string | null }) {
    if (!item) return;
    const body = { ...extra } as { local_name?: string; ref_id?: string | null };
    if (name.trim() !== item.local_name) body.local_name = name.trim();
    if (Object.keys(body).length === 0) return;
    update.mutate(
      { id: item.id, body },
      {
        onSuccess: () => {
          toast.success('Сохранено');
          onOpenChange(false);
        },
        onError: (e) => toast.error(e.message || 'Не удалось сохранить'),
      },
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            {item.local_name}
            {opClass && <Badge variant="outline">{opClass}</Badge>}
            {archived && <Badge variant="secondary">в архиве</Badge>}
          </SheetTitle>
          <SheetDescription>
            {archived
              ? `В архиве с ${formatRuDate(item.archived_at as string)}`
              : 'Как эта работа называется у вас, какой операции общего справочника соответствует и где название видно.'}
          </SheetDescription>
        </SheetHeader>

        {/* Название */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="cot-name">Как называется у нас</Label>
          <Input
            id="cot-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy || !canWrite}
          />
          <p className="text-xs text-muted-foreground">
            Название своё — можно сократить под привычку хозяйства. На то, чем
            работа является в системе, это не влияет.
          </p>
        </div>

        {/* Эталон — факт + гард перепривязки */}
        <div className="flex flex-col gap-2">
          <Label>В системе это</Label>
          <p className="text-sm text-foreground">
            {item.ref_name_ru ?? 'своя запись без привязки'}
            {opClass && (
              <span className="text-muted-foreground"> · {opClass.toLowerCase()}</span>
            )}
          </p>
          {relinkLocked ? (
            <Alert>
              <AlertDescription className="flex flex-col items-start gap-2">
                {!refs.isSuccess ? (
                  'Проверяем, где используется это название…'
                ) : (
                  <>
                    <span>
                      Название используется в {usedCount} записях, поэтому
                      привязку менять нельзя: у операций зафиксирован состав
                      полей, и смена эталона задним числом поменяла бы смысл уже
                      заведённой истории. Нужна другая операция — заархивируйте
                      это название и добавьте новое.
                    </span>
                    {canWrite && canArchive && onArchiveAndCreate && !archived && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => onArchiveAndCreate(item)}
                      >
                        Заархивировать и создать новое
                      </Button>
                    )}
                  </>
                )}
              </AlertDescription>
            </Alert>
          ) : (
            canWrite && (
              <div className="flex flex-col gap-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    placeholder="Привязать к другой операции — начните вводить название…"
                    value={relinkQuery}
                    onChange={(e) => setRelinkQuery(e.target.value)}
                    disabled={busy}
                  />
                </div>
                {relinkQuery.trim().length >= 2 && (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-border">
                    {relinkOptions.length === 0 ? (
                      <p className="p-3 text-sm text-muted-foreground">
                        Ничего не нашли. Попробуйте другое написание.
                      </p>
                    ) : (
                      <ul>
                        {relinkOptions.map((o) => (
                          <li key={o.id}>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => save({ ref_id: o.id })}
                              className={cn(
                                'flex min-h-10 w-full items-center px-3 py-2 text-left text-sm',
                                'hover:bg-accent',
                              )}
                            >
                              {o.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )
          )}
        </div>

        {/* Где видно — факт: смена уровня это raise_scope, а не PATCH */}
        <div className="flex flex-col gap-2">
          <Label>Где видно</Label>
          <p className="text-sm text-foreground">{cotVisibilityLabel(item)}</p>
          <p className="text-xs text-muted-foreground">
            Название видит учётчик там, где оно заведено. Переносить его на
            другой уровень отдельным действием — здесь показан факт, а не выбор.
          </p>
        </div>

        {/* Свои поля — решение founder 3 (конструктор в карточке) */}
        <div className="flex flex-col gap-2">
          <Label>Поля при заведении операции</Label>
          {ownFields.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Своих полей нет — записываются только системные.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {ownFields.map(({ scope, def }) => (
                <span
                  key={`${scope}:${def.key}`}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-foreground"
                >
                  <span className="font-medium">{def.label}</span>
                  <span className="text-muted-foreground">
                    {scope === 'operation' ? 'на операцию' : 'на смену'} ·{' '}
                    {fieldSummary(def)}
                  </span>
                  {canWrite && !archived && (
                    <button
                      type="button"
                      aria-label={`Убрать поле «${def.label}»`}
                      disabled={busy}
                      onClick={() =>
                        removeField.mutate(
                          { id: item.id, scope, key: def.key },
                          {
                            onSuccess: () => toast.success('Поле убрано'),
                            onError: (e) =>
                              toast.error(e.message || 'Не удалось убрать поле'),
                          },
                        )
                      }
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          {canWrite && !archived && (
            <Button
              variant="outline"
              size="sm"
              className="self-start"
              disabled={busy}
              onClick={() => setFieldDialog(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Добавить поле
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            Поля операции заполняются один раз, поля смены — на каждого
            исполнителя. Убранное поле перестаёт спрашиваться в новых операциях;
            в заведённых значение остаётся.
          </p>
        </div>

        {/* Как используется — числа без ссылок */}
        <div className="flex flex-col gap-2">
          <Label>Как используется</Label>
          <p className="text-sm text-muted-foreground">
            {operationsCountLabel(operations)}
            {norms > 0 && ` · норм выработки ${norms}`}
            {costLines > 0 && ` · строк тех.карт ${costLines}`}
          </p>
        </div>

        {costLines > 0 && !archived && (
          <Alert>
            <AlertTitle>Название стоит в планах</AlertTitle>
            <AlertDescription>
              На него ссылаются {costLines} строк тех.карт. Архивация уберёт
              название из выбора при новой операции, но плановые строки на нём
              останутся — сначала замените название в плане.
            </AlertDescription>
          </Alert>
        )}

        {/* Заявка в системный каталог — второй ряд, только пока привязки нет */}
        {!item.ref_id && !archived && (
          <div className="flex flex-col gap-2">
            <Label>Нет в системном каталоге?</Label>
            {request ? (
              <p className="text-sm text-muted-foreground">
                Заявка отправлена ·{' '}
                {request.status === 'pending'
                  ? 'ждёт platform-admin'
                  : request.status === 'rejected'
                    ? 'отклонена'
                    : 'разобрана'}
              </p>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start"
                  disabled={!canWrite || requestImport.isPending}
                  onClick={() =>
                    requestImport.mutate(
                      { incoming_name: item.local_name, company_op_type_id: item.id },
                      {
                        onSuccess: () =>
                          toast.success('Заявка отправлена — её разберёт platform-admin'),
                        onError: (e) =>
                          toast.error(e.message || 'Не удалось отправить заявку'),
                      },
                    )
                  }
                >
                  Запросить в системный каталог
                </Button>
                <p className="text-xs text-muted-foreground">
                  Отправится platform-admin: он заведёт операцию в общий каталог
                  или привяжет к существующей. Список заявок — на вкладке
                  «Интеграции и импорт».
                </p>
              </>
            )}
          </div>
        )}

        <div className="mt-auto flex flex-wrap justify-between gap-2">
          {canArchive &&
            (archived ? (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() =>
                  unarchive.mutate(item.id, {
                    onSuccess: () => {
                      toast.success('Название вернулось в работу');
                      onOpenChange(false);
                    },
                    onError: (e) => toast.error(e.message || 'Не удалось восстановить'),
                  })
                }
              >
                Вернуть из архива
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() =>
                  archive.mutate(item.id, {
                    onSuccess: () => {
                      toast.success('Название в архиве');
                      onOpenChange(false);
                    },
                    onError: (e) => toast.error(e.message || 'Не удалось заархивировать'),
                  })
                }
              >
                В архив
              </Button>
            ))}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button disabled={busy || !dirty || !canWrite} onClick={() => save()}>
              {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Сохранить
            </Button>
          </div>
        </div>

        <OpTypeFieldDialog
          item={item}
          open={fieldDialog}
          onOpenChange={setFieldDialog}
        />
      </SheetContent>
    </Sheet>
  );
}

export default CompanyOpTypeSheet;
